// CommandPalette — the global Cmd-K command palette overlay (RFC-0011 Task 2.2).
//
// Composes the Phase-1 primitives: a debounced search input (useSearch), the
// RepoScopeToggle as the scope chip, a listbox of SearchResultItem rows, and a
// "See all results →" affordance that hands off to the `/search` page carrying
// the current scope. Reads open/close + the route-resolved initial scope from
// useCommandPalette(); renders nothing when closed.
//
// Accessibility (the critical part):
//   - `role="combobox"` input + `role="listbox"` results + `role="option"` rows,
//     with `aria-activedescendant` tracking the active row (combobox pattern —
//     focus stays on the input; the list is navigated with the arrow keys).
//   - Arrow Up/Down move the active row (clamped, no wrap); Enter selects; Esc
//     closes. Mouse hover syncs the active row.
//   - Focus trap while open + focus restore to the previously-focused element on
//     close; click-outside (backdrop) closes.
//
// Security: bespoke palette — NO new runtime dependency (no cmdk/kbar), no eval,
// no inline scripts, no `dangerouslySetInnerHTML`. Snippet highlighting is handled
// inside SearchResultItem via the pure highlight parser; same-origin `/api/search`
// only (CSP `connect-src 'self'`).

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import type { SearchHit } from '@portal/shared-types';
import { useCommandPalette, type SearchScope } from '@/context/command-palette';
import { useSearch } from '@/hooks/use-search';
import { RepoScopeToggle } from '@/components/search/repo-scope-toggle';
import { SearchResultItem } from '@/components/search/search-result-item';
import { Skeleton } from '@/components/ui/query-skeletons';

/** Palette result cap — the full set lives on `/search` behind "See all results". */
const PALETTE_PER_PAGE = 10;
const LISTBOX_ID = 'command-palette-listbox';
const optionId = (index: number) => `command-palette-option-${index}`;

/** Focusable elements for the focus trap (result rows are combobox options, not tab stops). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Build the RFC-0010 doc-reader route from a hit's own repo/path (never a raw URL). */
function docHref(repo: string, path: string): string {
  return `/projects/${encodeURIComponent(repo)}/docs/${path}`;
}

/**
 * Mount gate: the dialog (with its state, effects, and useSearch) only exists
 * while the palette is open, so focus capture/restore happens on mount/unmount
 * and no request is scheduled while closed.
 */
export function CommandPalette() {
  const { isOpen } = useCommandPalette();
  if (!isOpen) return null;
  return <CommandPaletteDialog />;
}

function CommandPaletteDialog() {
  const { close, initialScope, repoName } = useCommandPalette();
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [scope, setScope] = useState<SearchScope>(initialScope);
  const [activeIndex, setActiveIndex] = useState(0);

  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  const trimmed = q.trim();
  const result = useSearch({
    q,
    repo: scope === 'repo' ? repoName : undefined,
    page: 1,
    perPage: PALETTE_PER_PAGE,
  });

  const hits: SearchHit[] = useMemo(() => result.data?.data ?? [], [result.data]);
  const total = result.data?.pagination.total ?? 0;

  // Display states (RFC §4). Idle short-circuits before any request is scheduled.
  const showIdle = trimmed.length === 0;
  const showLoading = !showIdle && result.isLoading;
  const showError = !showIdle && result.isError;
  const showResults = !showIdle && !showLoading && !showError && hits.length > 0;
  const showNoResults =
    !showIdle && !showLoading && !showError && result.data !== undefined && hits.length === 0;
  const showSeeAll = showResults && total > hits.length;

  // Keep the active row in range as results change; reset when the query/scope changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed, scope]);
  const clampedActive = hits.length > 0 ? Math.min(activeIndex, hits.length - 1) : 0;

  // Capture the invoking element on open; focus the input; restore focus on close.
  useEffect(() => {
    restoreRef.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      restoreRef.current?.focus?.();
    };
  }, []);

  // Body scroll-lock while the dialog is mounted (it only mounts while open),
  // restoring the previous value on every close path — mirrors the Drawer
  // primitive so the page can't scroll behind the palette on a phone.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function selectHit(hit: SearchHit) {
    close();
    navigate(docHref(hit.repo, hit.path));
  }

  function goToSearchPage() {
    const params = new URLSearchParams({ q: trimmed });
    if (scope === 'repo' && repoName) params.set('repo', repoName);
    close();
    navigate(`/search?${params.toString()}`);
  }

  function onDialogKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    // Focus trap: keep Tab within the dialog.
    if (event.key === 'Tab') {
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
      return;
    }

    if (!showResults) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      const hit = hits[clampedActive];
      if (hit) {
        event.preventDefault();
        selectHit(hit);
      }
    }
  }

  const activeDescendant = showResults ? optionId(clampedActive) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-3 pt-[10vh] sm:p-4 sm:pt-[12vh]"
      role="presentation"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-[var(--c-shadow-md)] sm:max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onDialogKeyDown}
      >
        {/* Input row: combobox + scope chip. */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <MagnifyingGlass size={18} className="shrink-0 text-muted" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search all projects"
            aria-expanded={showResults || showNoResults}
            aria-controls={LISTBOX_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendant}
            value={q}
            placeholder={
              scope === 'repo' && repoName ? `Search ${repoName}…` : 'Search all projects…'
            }
            onChange={(e) => setQ(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-muted"
          />
          <RepoScopeToggle
            scope={scope}
            repoName={repoName}
            onChange={(next) => setScope(next)}
            className="shrink-0"
          />
        </div>

        {/* Results / states. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
          {showIdle && (
            <p data-testid="command-palette-idle" className="px-3 py-6 text-center text-xs text-muted">
              Search across all your project docs.
            </p>
          )}

          {showLoading && (
            <div role="status" aria-label="Searching" className="flex flex-col gap-2 p-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-lg px-3 py-2">
                  <Skeleton className="mb-2 h-4 w-1/2" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ))}
            </div>
          )}

          {showError && (
            <p role="status" className="px-3 py-6 text-center text-sm text-muted">
              Search is temporarily unavailable. Please try again in a moment.
            </p>
          )}

          {showNoResults && (
            <div className="px-3 py-6 text-center text-sm text-muted">
              <p className="mb-2">No results for &ldquo;{trimmed}&rdquo;.</p>
              <button
                type="button"
                onClick={goToSearchPage}
                className="text-accent hover:underline"
              >
                Search the full portal &rarr;
              </button>
            </div>
          )}

          {showResults && (
            <ul id={LISTBOX_ID} role="listbox" aria-label="Search results" className="flex flex-col gap-0.5">
              {hits.map((hit, i) => (
                <li
                  key={hit.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === clampedActive}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <SearchResultItem
                    result={hit}
                    isActive={i === clampedActive}
                    onSelect={selectHit}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* See-all footer — carries the current query + scope to /search. */}
        {showSeeAll && (
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={goToSearchPage}
              className="w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-accent hover:bg-accent-faint"
            >
              See all {total} results &rarr;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
